<template>
  <button
    :class="['sprintly-btn', `btn-${variant}`, sizeClass, { 'btn-loading': loading, 'btn-block': block }]"
    :disabled="disabled || loading"
    @click="$emit('click', $event)"
  >
    <v-progress-circular
      v-if="loading"
      indeterminate
      size="16"
      width="2"
      :color="variant === 'primary' ? '#000' : '#fff'"
      class="btn-loader"
    />
    <v-icon v-if="icon && !loading" :size="iconSize" class="btn-icon">
      {{ icon }}
    </v-icon>
    <span v-if="!loading || showTextWhileLoading" class="btn-text">
      <slot></slot>
    </span>
  </button>
</template>

<script>
export default {
  name: 'SprintlyButton',
  props: {
    variant: {
      type: String,
      default: 'primary',
      validator: (value) => ['primary', 'secondary', 'danger', 'ghost', 'outline'].includes(value)
    },
    size: {
      type: String,
      default: 'medium',
      validator: (value) => ['small', 'medium', 'large'].includes(value)
    },
    icon: {
      type: String,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    block: {
      type: Boolean,
      default: false
    },
    showTextWhileLoading: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    sizeClass() {
      return `btn-${this.size}`;
    },
    iconSize() {
      const sizes = {
        small: 16,
        medium: 18,
        large: 20
      };
      return sizes[this.size];
    }
  }
}
</script>

<style lang="scss" scoped>
/* === BASE BUTTON === */
.sprintly-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  font-family: 'Inter', sans-serif;
  position: relative;
  overflow: hidden;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.btn-loading {
    pointer-events: none;
  }
}

/* === SIZES === */
.btn-small {
  padding: 6px 16px;
  font-size: 0.85rem;
}

.btn-medium {
  padding: 10px 24px;
  font-size: 0.9rem;
}

.btn-large {
  padding: 14px 32px;
  font-size: 1rem;
}

/* === BLOCK === */
.btn-block {
  width: 100%;
}

/* === VARIANTS === */
.btn-primary {
  background: #BC3C44;
  color: #FFFFFF;

  &:hover:not(:disabled) {
    background: #9a3139;
    box-shadow: 0 4px 12px rgba(188, 60, 68, 0.4);
  }
}

.btn-secondary {
  background: #2a2a2a;
  color: #ffffff;
  border: none;

  &:hover:not(:disabled) {
    background: #444444;
    border-color: #3a3e45;
  }
}

.theme--light .btn-secondary {
  background: #f0f0f0;
  color: #1a1a1a;
  border-color: #d1d5db;

  &:hover:not(:disabled) {
    background: #e0e0e0;
    border-color: #b0b0b0;
  }
}

.btn-danger {
  background: #e67e4e;
  color: #ffffff;

  &:hover:not(:disabled) {
    background: #d96d3e;
    box-shadow: 0 4px 12px rgba(230, 126, 78, 0.3);
  }
}

.btn-ghost {
  background: transparent;
  color: #999999;

  &:hover:not(:disabled) {
    background: #2a2a2a;
    color: #ffffff;
  }
}

.theme--light .btn-ghost {
  color: #666666;

  &:hover:not(:disabled) {
    background: #f0f0f0;
    color: #1a1a1a;
  }
}

.btn-outline {
  background: transparent;
  color: #BC3C44;
  border: 1px solid #BC3C44;

  &:hover:not(:disabled) {
    background: rgba(188, 60, 68, 0.1);
    border-color: #9a3139;
  }
}

/* === ELEMENTS === */
.btn-loader {
  position: relative;
}

.btn-icon {
  display: flex;
  align-items: center;
}

.btn-text {
  display: inline-flex;
  align-items: center;
}

/* === RESPONSIVE === */
@media (max-width: 600px) {
  .btn-small {
    padding: 5px 12px;
    font-size: 0.8rem;
  }

  .btn-medium {
    padding: 8px 20px;
    font-size: 0.85rem;
  }

  .btn-large {
    padding: 12px 28px;
    font-size: 0.95rem;
  }
}
</style>
